import React, { useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { order2Sorting, TableCellV8, TablePaginatedV8, EyeColumnV8 } from 'motion-components';
import { useLocation } from 'react-router-dom';
import { generatePath, useHistory } from 'react-router';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  isActiveColumn,
} from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { notificationTypeLabel, notificationResponseTypeLabel } from '../constants';
import { NotificationStatusBadge } from '../notification-status-badge';

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    dataCount,
  } = props;

  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Notification'), { id: row.id }),
        state: {
          pathname: location.pathname,
          ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'name',
        header: t('Όνομα'),
        accessorKey: 'name',
        orderByField: 'name',
        columnName: t('Όνομα'),
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 28,
      },
      {
        id: 'typeCode',
        header: t('Τύπος'),
        accessorKey: 'typeCode',
        orderByField: 'typeCode',
        columnName: t('Τύπος'),
        cell: ({ row }) => <span>{notificationTypeLabel(row.original.typeCode)}</span>,
        size: 18,
      },
      {
        id: 'responseType',
        header: t('Απάντηση'),
        accessorKey: 'responseType',
        orderByField: 'responseType',
        columnName: t('Απάντηση'),
        cell: ({ row }) => <span>{notificationResponseTypeLabel(row.original.responseType)}</span>,
        size: 12,
      },
      {
        id: 'statusId',
        header: t('Κατάσταση'),
        accessorKey: 'statusId',
        orderByField: 'statusId',
        columnName: t('Κατάσταση'),
        cell: ({ row }) => <NotificationStatusBadge statusId={row.original.statusId} />,
        size: 14,
      },
      {
        id: 'tokenTtlDays',
        header: t('Ημέρες token'),
        accessorKey: 'tokenTtlDays',
        orderByField: 'tokenTtlDays',
        columnName: t('Ημέρες token'),
        cell: TableCellV8,
        size: 10,
      },
      isActiveColumn(t, i18n),
      firstDateUserColumn(t),
    ],
    [t, i18n.language, rowClicked],
  );

  const tableRef = useRef(null);
  useImperativeHandle(ref, () => tableRef.current || null);

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort, columnDef, orderBy) => {
            onOrderChange(orderBy);
          }}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
